// src/components/admin/ColorManager.tsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PhotoCamera,
  Palette,
} from '@mui/icons-material';
import { 
  useGetProductColorsQuery, 
  useAddProductColorMutation, 
  useUpdateProductColorMutation, 
  useDeleteProductColorMutation 
} from '../../features/admin/productApi.ts';

interface ColorManagerProps {
  productId: number;
}

const ColorManager: React.FC<ColorManagerProps> = ({ productId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingColor, setEditingColor] = useState<any>(null);
  const [colorForm, setColorForm] = useState({
    color: '',
    price: 0,
    retail_price: 0,
    color_image: null as File | null,
  });
  const [imagePreview, setImagePreview] = useState<string>('');

  const { data: colorsResponse, refetch } = useGetProductColorsQuery({ product_id: productId });
  const [addColor] = useAddProductColorMutation();
  const [updateColor] = useUpdateProductColorMutation();
  const [deleteColor] = useDeleteProductColorMutation();

  const colors = colorsResponse?.data || [];

  const handleAddColor = () => {
    setColorForm({
      color: '',
      price: 0,
      retail_price: 0,
      color_image: null,
    });
    setImagePreview('');
    setEditingColor(null);
    setDialogOpen(true);
  };

  const handleEditColor = (color: any) => {
    setColorForm({
      color: color.color,
      price: color.price,
      retail_price: color.retail_price,
      color_image: null,
    });
    setImagePreview(color.color_image || '');
    setEditingColor(color);
    setDialogOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setColorForm(prev => ({ ...prev, color_image: file }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setColorForm(prev => ({ ...prev, color_image: null }));
    setImagePreview('');
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('product_id', productId.toString());
      formData.append('color', colorForm.color);
      formData.append('price', colorForm.price.toString());
      formData.append('retail_price', colorForm.retail_price.toString());
      
      if (colorForm.color_image) {
        formData.append('color_image', colorForm.color_image);
      }
      
      if (editingColor) {
        formData.append('id', editingColor.id.toString());
        await updateColor(formData).unwrap();
      } else {
        await addColor(formData).unwrap();
      }
      
      setDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error saving color:', error);
    }
  };

  const handleDeleteColor = async (colorId: number) => {
    if (window.confirm('Are you sure you want to delete this color?')) {
      try {
        await deleteColor({ product_id: productId, id: colorId }).unwrap();
        refetch();
      } catch (error) {
        console.error('Error deleting color:', error);
      }
    }
  };

  // Predefined color options
  const colorOptions = [
    { name: 'Red', value: '#ff0000' },
    { name: 'Blue', value: '#0000ff' },
    { name: 'Green', value: '#00ff00' },
    { name: 'Yellow', value: '#ffff00' },
    { name: 'Purple', value: '#800080' },
    { name: 'Orange', value: '#ffa500' },
    { name: 'Black', value: '#000000' },
    { name: 'White', value: '#ffffff' },
    { name: 'Pink', value: '#ffc0cb' },
    { name: 'Brown', value: '#a52a2a' },
    { name: 'Gray', value: '#808080' },
    { name: 'Gold', value: '#ffd700' },
    { name: 'Silver', value: '#c0c0c0' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Color Variants</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddColor}
        >
          Add Color
        </Button>
      </Box>

      <Grid container spacing={2}>
        {colors.map((color) => (
          <Grid item xs={12} sm={6} md={4} key={color.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {color.color_image && (
                    <Avatar
                      src={`https://fftestservice.sarsetech.com/images/${color.color_image}`}
                      sx={{ width: 40, height: 40, mr: 2 }}
                    />
                  )}
                  <Chip
                    label={color.color}
                    sx={{
                      backgroundColor: colorOptions.find(c => c.name.toLowerCase() === color.color.toLowerCase())?.value || '#ccc',
                      color: '#fff',
                      fontWeight: 'bold',
                    }}
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Price: ${color.price}
                  </Typography>
                  <Typography variant="body2">
                    Retail: ${color.retail_price}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditColor(color)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteColor(color.id)}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {colors.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Palette sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                No color variants added yet
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingColor ? 'Edit Color' : 'Add Color'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Color Name"
              value={colorForm.color}
              onChange={(e) => setColorForm({ ...colorForm, color: e.target.value })}
              fullWidth
              select
              SelectProps={{
                native: true,
              }}
            >
              <option value="">Select a color</option>
              {colorOptions.map((color) => (
                <option key={color.value} value={color.name}>
                  {color.name}
                </option>
              ))}
            </TextField>
            
            <TextField
              label="Price"
              type="number"
              value={colorForm.price}
              onChange={(e) => setColorForm({ ...colorForm, price: Number(e.target.value) })}
              fullWidth
            />
            
            <TextField
              label="Retail Price"
              type="number"
              value={colorForm.retail_price}
              onChange={(e) => setColorForm({ ...colorForm, retail_price: Number(e.target.value) })}
              fullWidth
            />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                src={imagePreview}
                sx={{ width: 80, height: 80, mb: 2 }}
                variant="rounded"
              >
                {!imagePreview && <PhotoCamera />}
              </Avatar>
              
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
              
              {imagePreview && (
                <Button
                  color="error"
                  onClick={handleRemoveImage}
                  sx={{ mt: 1 }}
                >
                  Remove Image
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingColor ? 'Update' : 'Add'} Color
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ColorManager;